import Input from '@/components/Common/Input';
import TelInput from '@/components/Common/TelInput';
import BusinessHoursSection from '@/pages/Business/Setting/Locations/components/businessHours/BusinessHourSelection';
import { Box, Typography, Paper, Button, Divider, Grid2 as Grid, IconButton } from '@mui/material';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { RiDeleteBin6Line } from 'react-icons/ri';

export default function OperatorDetailsForm() {
  const { control, getValues, setValue } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'operator_details'
  });

  return (
    <Paper sx={{ border: '1px solid #EAEAEA', borderRadius: 2, p: { xs: 2, md: 4 } }}>
      <Typography fontWeight={600} fontSize={18} mb={0.5}>
        Member Details
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={3}>
        Add the people who will handle calls transferred by the agent.
      </Typography>

      <Box
        sx={{
          background: '#FFFBEB',
          borderRadius: 1,
          p: '12px',
          mb: 3,
          color: '#D97706',
          fontSize: 14
        }}
      >
        NOTE - The agent escalates calls to operators based on availability and working hours.
      </Box>

      {fields.map((field, index) => (
        <Box key={field.id} mb={4}>
          <OperatorCard index={index} onRemove={remove} getValues={getValues} setValue={setValue} />

          {index !== fields.length - 1 && <Divider sx={{ mt: 4 }} />}
        </Box>
      ))}

      <Button
        variant='outlined'
        sx={{
          borderRadius: 20,
          textTransform: 'none',
          borderColor: '#D1D5DB'
        }}
        onClick={() =>
          append({
            name: '',
            email: '',
            phone: '',
            extension: '',
            sip_uri: '',
            sms_consent: false,
            working_hours: {}
          })
        }
      >
        + Add Another Operator
      </Button>
    </Paper>
  );
}

interface Props {
  index: number;
  onRemove: (index: number) => void;
  getValues: any;
  setValue: any;
}

function OperatorCard({ index, onRemove, getValues, setValue }: Props) {
  const { control } = useFormContext();

  return (
    <Paper variant='outlined' sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Box>
          <Typography fontSize={16} fontWeight={500} mb={1}>
            Operator #{index + 1}
          </Typography>

          <Typography variant='body2' color='text.secondary'>
            Add the operator's contact details for call transfers.
          </Typography>
        </Box>

        {index > 0 && (
          <IconButton color='error' onClick={() => onRemove(index)}>
            <RiDeleteBin6Line />
          </IconButton>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={6}>
          <Input
            name={`operator_details.${index}.name`}
            label='Operator Name'
            control={control}
            required
            fullWidth
            placeholder='Full Name'
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`operator_details.${index}.email`}
            label='Primary Contact Email'
            control={control}
            required
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TelInput
            name={`operator_details.${index}.phone`}
            label='Primary Contact Phone'
            control={control}
            consent_key={`operator_details.${index}.sms_consent`}
            required
            defaultCountry='US'
            fullWidth
          />
          <Typography color='secondary' fontSize={12}>
            Use a different number then the one assigned to your agent.
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`operator_details.${index}.extension`}
            label='Extension Number'
            control={control}
            helperText='Used to route calls to a specific user or department. Leave blank if you’re unsure.'
            fullWidth
            placeholder='Enter Extension Number'
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Input
            name={`operator_details.${index}.sip_uri`}
            label='SIP URI'
            control={control}
            helperText="Used for SIP-based call routing. Leave blank if you're unsure."
            fullWidth
            placeholder='sip:user@domain.com'
          />
        </Grid>
      </Grid>

      <Typography fontWeight={600} mt={4}>
        Working Hours
      </Typography>

      <Typography variant='body2' color='text.secondary' mb={2}>
        Set availability so the agent transfers calls only when the operator is reachable.
      </Typography>

      <BusinessHoursSection
        control={control}
        fieldName={`operator_details.${index}.working_hours`}
        getValues={getValues}
        viewLabel={false}
        setValue={setValue}
      />
    </Paper>
  );
}
